import { workflowCompleteCommandAdapter } from '@app/shared/workflow-commands/workflow-complete-command-adapter';
import { isBlockOwnedCommand } from '@app/phms/workflow-blocks/domain-block-manifests';
import type {
  WorkflowDefinitionDocument,
  WorkflowDefinitionNode,
} from './platform-workflow-definition-lifecycle.models';

/** Exact saved identity; it says nothing about the current provider installation. */
export interface WorkflowInspectionCommand {
  key: string;
  label: string;
  reference: string;
  pins: readonly { label: string; value: string }[];
}

/** Forms identifiable from authored settings or the exact saved main interaction. */
export interface WorkflowInspectionForm extends WorkflowInspectionCommand {
  detail: string;
}

/** One retained activity-result mapping, without resolving runtime conditions. */
export interface WorkflowInspectionResult {
  key: string;
  label: string;
  commandOutcome: string;
  routeOutcome: string;
  destination: string;
  detail: string;
  needsInspection: boolean;
}

/** Read-only inventory of authored step settings, never a catalog or runtime result. */
export interface WorkflowStepInspectionPresentation {
  hasDeclaration: boolean;
  forms: WorkflowInspectionForm[];
  actions: WorkflowInspectionCommand[];
  moduleCommands: WorkflowInspectionCommand[];
  formNote: string;
  activity: WorkflowInspectionCommand | null;
  activityNote: string;
  results: WorkflowInspectionResult[];
}

const object = (value: unknown): Record<string, unknown> | null =>
  !!value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
const text = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';
const savedText = (value: unknown): string =>
  typeof value === 'string' ? value : '';
const entries = (value: unknown): unknown[] =>
  Array.isArray(value) ? value : [];
const version = (value: unknown): string =>
  typeof value === 'number' && Number.isSafeInteger(value) && value > 0
    ? String(value)
    : 'unavailable';

function exactReference(left: unknown, right: unknown): boolean {
  const a = object(left),
    b = object(right),
    ak = object(a?.['key']),
    bk = object(b?.['key']);
  return (
    !!a &&
    !!b &&
    !!ak &&
    !!bk &&
    ['provider', 'kind', 'code'].every(
      (key) => !!text(ak[key]) && ak[key] === bk[key],
    ) &&
    version(ak['version']) !== 'unavailable' &&
    ak['version'] === bk['version'] &&
    ['contractHash', 'inputSchemaHash', 'outputSchemaHash'].every(
      (key) => !!text(a[key]) && a[key] === b[key],
    )
  );
}

function commandSummary(
  value: unknown,
  index: number,
): WorkflowInspectionCommand {
  const reference = object(value),
    key = object(reference?.['key']);
  const code = savedText(key?.['code']);
  return {
    key: `command:${index}`,
    label: code || 'Incomplete saved command',
    reference:
      [
        savedText(key?.['provider']) || 'Unknown provider',
        savedText(key?.['kind']) || 'Unknown kind',
        code || 'Unknown command',
      ].join(' / ') + ` · v${version(key?.['version'])}`,
    pins: [
      {
        label: 'Contract hash',
        value: savedText(reference?.['contractHash']) || 'Not recorded',
      },
      {
        label: 'Input schema hash',
        value: savedText(reference?.['inputSchemaHash']) || 'Not recorded',
      },
      {
        label: 'Output schema hash',
        value: savedText(reference?.['outputSchemaHash']) || 'Not recorded',
      },
    ],
  };
}

/** Apply Work's known ownership rules without guessing at unknown saved identities. */
function commandOwnership(value: unknown): 'module' | 'completion' | null {
  const command = object(value),
    capability = object(command?.['capability']),
    key = object(capability?.['key']);
  if (
    !key ||
    key['kind'] !== 'command' ||
    typeof key['provider'] !== 'string' ||
    typeof key['code'] !== 'string' ||
    typeof key['version'] !== 'number'
  )
    return null;
  const savedKey = {
    provider: key['provider'],
    kind: 'command',
    code: key['code'],
    version: key['version'],
  };
  if (isBlockOwnedCommand(savedKey)) return 'module';
  return workflowCompleteCommandAdapter.matches({
    key: savedKey,
    contractHash: savedText(capability?.['contractHash']),
    inputSchemaHash: savedText(capability?.['inputSchemaHash']),
    outputSchemaHash: savedText(capability?.['outputSchemaHash']),
  })
    ? 'completion'
    : null;
}

function readableResult(code: string): string {
  const words = code.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/_/g, ' ');
  return words
    ? words[0].toUpperCase() + words.slice(1)
    : 'Incomplete saved result';
}

function activityResults(
  document: WorkflowDefinitionDocument | null,
  node: WorkflowDefinitionNode,
  mappings: unknown[],
): WorkflowInspectionResult[] {
  const nodes = entries(document?.nodes)
    .map(object)
    .filter((item): item is Record<string, unknown> => item !== null);
  const edges = entries(document?.edges)
    .map(object)
    .filter((item): item is Record<string, unknown> => item !== null);
  return mappings.map((value, index) => {
    const mapping = object(value),
      commandOutcome = savedText(mapping?.['commandOutcome']),
      routeOutcome = savedText(mapping?.['routeOutcome']);
    const result: WorkflowInspectionResult = {
      key: `result:${index}`,
      label: readableResult(commandOutcome),
      commandOutcome: commandOutcome || 'Not recorded',
      routeOutcome: routeOutcome || 'Not recorded',
      destination: 'Needs inspection',
      detail: '',
      needsInspection: true,
    };
    if (!document)
      return {
        ...result,
        detail:
          'The working document is unavailable for destination inspection.',
      };
    if (!text(commandOutcome) || !text(routeOutcome))
      return {
        ...result,
        detail: 'This saved result mapping is incomplete. Inspect Routing.',
      };
    if (
      !text(node.id) ||
      nodes.filter((item) => item['id'] === node.id).length !== 1
    )
      return {
        ...result,
        detail:
          'The source step identity is missing or ambiguous. Inspect the process map.',
      };
    if (
      mappings.filter(
        (item) =>
          savedText(object(item)?.['commandOutcome']) === commandOutcome ||
          savedText(object(item)?.['routeOutcome']) === routeOutcome,
      ).length !== 1
    )
      return {
        ...result,
        detail:
          'This saved result mapping shares an outcome identity. Inspect Routing.',
      };
    const paths = edges.filter(
      (edge) =>
        edge['fromNodeId'] === node.id && edge['outcomeCode'] === routeOutcome,
    );
    if (!paths.length)
      return {
        ...result,
        destination: 'No configured destination',
        detail: 'No path matches this saved route outcome. Inspect Routing.',
      };
    if (paths.length !== 1)
      return {
        ...result,
        destination: 'Multiple matching paths',
        detail:
          'More than one path matches this saved route outcome. Inspect the process map.',
      };
    const path = paths[0],
      destinationId = savedText(path['toNodeId']);
    if (
      !text(path['id']) ||
      edges.filter((edge) => edge['id'] === path['id']).length !== 1
    )
      return {
        ...result,
        destination: 'Path identity needs inspection',
        detail:
          'The matching path identity is missing or duplicated. Inspect the process map.',
      };
    const destinations = nodes.filter((item) => item['id'] === destinationId);
    if (!text(destinationId) || !destinations.length)
      return {
        ...result,
        destination: destinationId
          ? `Missing destination (${destinationId})`
          : 'No destination recorded',
        detail: 'The saved path destination needs repair in Routing.',
      };
    if (destinations.length !== 1)
      return {
        ...result,
        destination: `Ambiguous destination (${destinationId})`,
        detail:
          'More than one node uses this destination identity. Inspect the process map.',
      };
    const destination = destinations[0];
    const label =
      text(destination['displayName']) ||
      text(destination['stepCode']) ||
      destinationId;
    const pathKind = path['pathKindCode'];
    if (destinationId === node.id)
      return {
        ...result,
        destination: label,
        detail: 'This saved activity path points to itself. Inspect Routing.',
      };
    if (
      !['human_step', 'automated_step', 'terminal'].includes(
        text(destination['typeCode']),
      )
    )
      return {
        ...result,
        destination: label,
        detail:
          'This saved destination uses routing or a retained node kind. Inspect the process map for its meaning.',
      };
    return {
      ...result,
      destination:
        destination['typeCode'] === 'terminal' &&
        !/^(complete|complete workflow)$/i.test(label)
          ? `Complete workflow · ${label}`
          : label,
      detail:
        pathKind === 'return'
          ? 'Retained direct return path. Automated results require ordinary outgoing paths; inspect Routing.'
          : pathKind == null
            ? 'Saved path. Runtime completion and routing checks remain authoritative.'
            : `Retained path kind: ${text(pathKind) || 'unreadable'}. Inspect Routing for its meaning.`,
      needsInspection: pathKind != null,
    };
  });
}

/** Preserve every saved entry; summarize only metadata available in this document. */
export function workflowStepInspectionPresentation(
  document: WorkflowDefinitionDocument | null,
  node: WorkflowDefinitionNode,
): WorkflowStepInspectionPresentation {
  const declaration = object(node.declaration),
    behavior = object(declaration?.['behavior']);
  const commands = entries(behavior?.['commands']);
  const interaction = object(
    object(declaration?.['detail'])?.['mainInteraction'],
  );
  const interactionMatches = interaction
    ? commands.filter((command) =>
        exactReference(object(command)?.['capability'], interaction['command']),
      )
    : [];
  const forms: WorkflowInspectionForm[] = [],
    actions: WorkflowInspectionCommand[] = [],
    moduleCommands: WorkflowInspectionCommand[] = [];
  commands.forEach((value, index) => {
    const command = object(value),
      summary = commandSummary(command?.['capability'], index);
    const ownership = commandOwnership(value);
    if (ownership === 'module') {
      moduleCommands.push(summary);
      return;
    }
    if (ownership === 'completion') {
      actions.push(summary);
      return;
    }
    const capture = command?.['captureForm'],
      configuration = command?.['formConfiguration'];
    if (capture != null) {
      const form = object(capture);
      forms.push({
        ...summary,
        label: text(form?.['title']) || 'Untitled capture form',
        detail:
          form?.['version'] === 1 && Array.isArray(form['fields'])
            ? `Capture form · ${form['fields'].length} authored field entr${form['fields'].length === 1 ? 'y' : 'ies'}.`
            : 'Retained form settings. Inspect Work for their supported meaning.',
      });
    } else if (
      configuration != null ||
      (interactionMatches.length === 1 &&
        exactReference(command?.['capability'], interaction?.['command']))
    ) {
      const configured = object(configuration);
      forms.push({
        ...summary,
        detail:
          configuration != null
            ? configured?.['version'] === 1 &&
              Array.isArray(configured['fields'])
              ? 'Provided form settings. Installed field definitions are checked in Work.'
              : 'Retained provided-form settings. Inspect Work for their supported meaning.'
            : `Main form interaction · ${text(interaction?.['code']) || 'Unspecified interaction'} · v${version(interaction?.['version'])}.`,
      });
    } else actions.push(summary);
  });
  const automation = object(node.automation);
  return {
    hasDeclaration: !!behavior && Array.isArray(behavior['commands']),
    forms,
    actions,
    moduleCommands,
    formNote:
      interaction && interactionMatches.length !== 1
        ? 'The main form interaction does not identify one exact saved action. Inspect Work.'
        : actions.length
          ? 'Forms shown here have authored settings or a main form interaction. Other installed form details are checked in Work.'
          : '',
    activity:
      node.typeCode === 'automated_step' && automation
        ? commandSummary(automation['command'], 0)
        : null,
    activityNote:
      node.typeCode === 'automated_step'
        ? automation
          ? `Saved activity configuration v${version(automation['version'])}. Current installation and worker authority are checked separately.`
          : 'No saved activity configuration. Open Activity to inspect the available controls.'
        : '',
    results:
      node.typeCode === 'automated_step' && automation
        ? activityResults(document, node, entries(automation['outcomes']))
        : [],
  };
}
